/**
 * Export filename utility
 *
 * Generates standardized filenames for exported reports following the pattern:
 * "Datasource-Tenant-Timeframe.extension"
 *
 * Example: "OpenTrack-OurismanChantillyToyota-July.xlsx"
 */

export type DataSource =
  | 'OpenTrack'
  | 'DealerVault'
  | 'ClassificationLogs'
  | 'AuditLog'
  | 'Misalignment';

/**
 * Formats a tenant name for use in export filenames.
 * Removes spaces and special characters, keeps alphanumeric characters.
 *
 * @example
 * formatTenantName("Ourisman Toyota Chantilly") => "OurismanToyotaChantilly"
 * formatTenantName("ourisman-chantilly") => "OurismanChantilly"
 */
export function formatTenantName(tenantName: string | undefined | null): string {
  if (!tenantName) return 'AllTenants';

  // Remove common prefixes that might be redundant
  const name = tenantName;

  // Split by spaces, hyphens, or underscores
  const parts = name.split(/[\s\-_]+/);

  // Capitalize each part and join
  return parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join('');
}

/**
 * Gets the month name from a date string in YYYY-MM-DD format.
 */
function getMonthName(dateStr: string): string {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const match = dateStr.match(/^\d{4}-(\d{2})/);
  if (!match) return dateStr;

  const monthIndex = parseInt(match[1], 10) - 1;
  return months[monthIndex] || dateStr;
}

/**
 * Gets the year from a date string in YYYY-MM-DD format.
 */
function getYear(dateStr: string): string {
  const match = dateStr.match(/^(\d{4})/);
  return match ? match[1] : '';
}

/**
 * Formats the timeframe based on start and end dates.
 *
 * @example
 * formatTimeframe("2025-07-01", "2025-07-31") => "July2025"
 * formatTimeframe("2025-07-01", "2025-08-15") => "July-August2025"
 * formatTimeframe("2025-12-01", "2026-01-31") => "December2025-January2026"
 */
export function formatTimeframe(
  startDate: string | undefined,
  endDate: string | undefined,
): string {
  if (!startDate) {
    return new Date().toISOString().split('T')[0];
  }

  const startMonth = getMonthName(startDate);
  const startYear = getYear(startDate);

  if (!endDate) {
    return `${startMonth}${startYear}`;
  }

  const endMonth = getMonthName(endDate);
  const endYear = getYear(endDate);

  // Same month and year
  if (startMonth === endMonth && startYear === endYear) {
    return `${startMonth}${startYear}`;
  }

  // Same year, different months
  if (startYear === endYear) {
    return `${startMonth}-${endMonth}${startYear}`;
  }

  // Different years
  return `${startMonth}${startYear}-${endMonth}${endYear}`;
}

export type ExportFormat = 'csv' | 'xlsx' | 'pdf';

/**
 * Gets the file extension for the export format.
 */
function getExtension(format: ExportFormat | 'excel'): string {
  if (format === 'excel' || format === 'xlsx') return 'xlsx';
  if (format === 'pdf') return 'pdf';
  return 'csv';
}

export interface ExportFilenameOptions {
  dataSource: DataSource;
  tenantName?: string | null;
  startDate?: string;
  endDate?: string;
  format: ExportFormat | 'excel';
  /** Optional suffix to append before extension (e.g., '-RAW' for raw exports) */
  suffix?: string;
}

/**
 * Generates a standardized export filename.
 *
 * @example
 * generateExportFilename({
 *   dataSource: 'OpenTrack',
 *   tenantName: 'Ourisman Toyota Chantilly',
 *   startDate: '2025-07-01',
 *   endDate: '2025-07-31',
 *   format: 'xlsx'
 * }) => "OpenTrack-OurismanToyotaChantilly-July2025.xlsx"
 *
 * @example
 * generateExportFilename({
 *   dataSource: 'OpenTrack',
 *   tenantName: 'Ourisman Toyota',
 *   startDate: '2025-07-01',
 *   format: 'csv',
 *   suffix: '-RAW'
 * }) => "OpenTrack-OurismanToyota-July2025-RAW.csv"
 */
export function generateExportFilename(options: ExportFilenameOptions): string {
  const { dataSource, tenantName, startDate, endDate, format, suffix = '' } = options;

  const formattedTenant = formatTenantName(tenantName);
  const timeframe = formatTimeframe(startDate, endDate);
  const extension = getExtension(format);

  return `${dataSource}-${formattedTenant}-${timeframe}${suffix}.${extension}`;
}
